import React, {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Typography } from 'antd';
import { useLocation, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors } from 'farmx-redux-core';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import './blockDetailPage.css';

const ERROR_STYLE = {
  backgroundColor: '#E23549',
  color: 'white',
};

const WARNING_STYLE = {
  backgroundColor: '#FFCC00',
  color: 'black',
};

const OK_STYLE = {
  backgroundColor: '#3DC873',
  color: 'black',
};

const INFO_STYLE = {
  backgroundColor: '#4F7BFF',
  color: 'white',
};

const getFirstNumber = (data, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = data?.[keys[i]];
    if (value !== undefined && value !== null && value !== '') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) return numericValue;
    }
  }
  return null;
};

const getDisplayValue = (value) => (
  value === undefined || value === null || value === '' ? '' : value
);

const getTotalBlocks = (data) => getFirstNumber(data, [
  'blockCount',
  'blocksCount',
  'totalBlocks',
  'totalBlockCount',
]);

const getRatioValues = (data, numeratorKeys) => {
  const total = getTotalBlocks(data);
  const numerator = getFirstNumber(data, numeratorKeys);
  if (total === null || numerator === null || total <= 0) return null;
  return { numerator, total };
};

const getCountValues = (data, onlineKeys, totalKeys) => {
  const online = getFirstNumber(data, onlineKeys);
  const total = getFirstNumber(data, totalKeys);
  if (online === null || total === null || total <= 0) return null;
  return { online, total };
};

const getCountStyle = (counts) => {
  if (!counts) return {};
  if (counts.online <= 0) return ERROR_STYLE;
  if (counts.online < counts.total) return WARNING_STYLE;
  return OK_STYLE;
};

const getRatioStyle = (ratio) => {
  if (!ratio) return {};
  if (ratio.numerator <= 0) return ERROR_STYLE;
  if (ratio.numerator < ratio.total) return WARNING_STYLE;
  return OK_STYLE;
};

const getAnomalyStyle = (score) => {
  if (score === null) return {};
  if (score <= 0) return INFO_STYLE;
  if (score < 50) return WARNING_STYLE;
  return ERROR_STYLE;
};

const renderStatusBadge = (text, style) => {
  if (!text) return <Typography.Text>{text}</Typography.Text>;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 9999,
        fontWeight: 600,
        minWidth: 72,
        textAlign: 'center',
        ...style,
      }}
    >
      {text}
    </span>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="block-detail-display-single">
    <Typography.Text strong>{`${label}:`}</Typography.Text>
    <Typography.Text>{value}</Typography.Text>
  </div>
);

const DetailBadgeItem = ({ label, text, style }) => (
  <div className="block-detail-display-single">
    <Typography.Text strong>{`${label}:`}</Typography.Text>
    {renderStatusBadge(text, style)}
  </div>
);

export const RanchDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = params || {};
  const contactSectionRef = useRef(null);
  const ranchDetails = useSelector((state) => selectors.selectSensorStats(state, 'ranch', id));
  const { loading, name } = ranchDetails || {};
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const contactOnlyView = searchParams.get('section') === 'contact';

  useEffect(() => {
    if (id) {
      dispatch(actions.loadRanchStatsSingle({ type: 'ranch', identifier: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (loading) return;
    if (contactOnlyView && contactSectionRef.current) {
      contactSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [contactOnlyView, loading]);

  const title = useMemo(() => {
    if (name) {
      return `${t('Ranch Detail')} - ${name}`;
    }
    return t('Ranch Detail');
  }, [name, t]);

  const irrigRecRatio = getRatioValues(ranchDetails, [
    'recommendationsHealthyBlockCount',
  ]);

  const satelliteImgRatio = getRatioValues(ranchDetails, [
    'imageryHealthyBlockCount',
  ]);

  const gatewayCounts = getCountValues(
    ranchDetails,
    ['gatewayOnlineCount'],
    ['gatewayTotalCount'],
  );

  const nodeCounts = getCountValues(
    ranchDetails,
    ['nodeOnlineCount'],
    ['nodeTotalCount'],
  );

  const sensorCounts = getCountValues(
    ranchDetails,
    ['sensorOnlineCount'],
    ['sensorTotalCount'],
  );

  const anomalyScore = getFirstNumber(ranchDetails, ['anomalyScore']);
  const latitude = ranchDetails?.latitude;
  const longitude = ranchDetails?.longitude;
  const locationText = (
    latitude !== undefined && latitude !== null && longitude !== undefined && longitude !== null
      ? `${latitude}, ${longitude}`
      : ''
  );

  return (
    <div className="block-detail-page ranch-detail-page">
      <Typography.Title level={3}>{title}</Typography.Title>
      {loading ? (
        <LoadingOutlined className="block-page-loader" />
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {contactOnlyView && (
            <div>
              <Typography.Title level={4}>{t('Contact Details')}</Typography.Title>
              <DetailItem
                label={t('Ranch Contact')}
                value={getDisplayValue(ranchDetails?.ranchContact)}
              />
              <DetailItem
                label={t('Phone')}
                value={getDisplayValue(ranchDetails?.phone)}
              />
              <DetailItem
                label={t('Email')}
                value={getDisplayValue(ranchDetails?.email)}
              />
              <Typography.Title level={4} style={{ marginTop: 16 }}>
                {t('Access Details')}
              </Typography.Title>
              <DetailItem
                label={t('Address')}
                value={getDisplayValue(ranchDetails?.address)}
              />
              <DetailItem
                label={t('Access Times')}
                value={getDisplayValue(ranchDetails?.accessTimes)}
              />
              <DetailItem
                label={t('Access Notes')}
                value={getDisplayValue(ranchDetails?.accessNotes)}
              />
            </div>
          )}

          {!contactOnlyView && (
            <div>
              <Typography.Title level={4}>{t('Basic Info')}</Typography.Title>
              <DetailItem label={t('Ranch ID')} value={getDisplayValue(ranchDetails?.id || id)} />
              <DetailItem label={t('Name')} value={getDisplayValue(ranchDetails?.name)} />
              <DetailItem
                label={t('Entity Name')}
                value={getDisplayValue(ranchDetails?.entityName)}
              />
              <DetailItem
                label={t('Entity ID')}
                value={getDisplayValue(ranchDetails?.entityId)}
              />
              <DetailItem
                label={t('Entity Priority')}
                value={getDisplayValue(
                  ranchDetails?.entityPriority
                  ?? ranchDetails?.priority,
                )}
              />
              <DetailItem label={t('Subscription')} value={getDisplayValue(ranchDetails?.subscription)} />
              <DetailItem
                label={t('Area acres')}
                value={getDisplayValue(ranchDetails?.areaAcres)}
              />
              <DetailItem
                label={t('Install State')}
                value={getDisplayValue(ranchDetails?.installState)}
              />
              <DetailItem label={t('Status')} value={String(Boolean(ranchDetails?.isActive))} />
            </div>
          )}

          {!contactOnlyView && <div ref={contactSectionRef} />}

          {!contactOnlyView && (
            <>
              <div>
                <Typography.Title level={4}>{t('Service Status')}</Typography.Title>
                <DetailBadgeItem
                  label={t('Irrig. Rec.')}
                  text={irrigRecRatio ? `${irrigRecRatio.numerator} / ${irrigRecRatio.total}` : ''}
                  style={getRatioStyle(irrigRecRatio)}
                />
                <DetailBadgeItem
                  label={t('Satellite Img.')}
                  text={satelliteImgRatio ? `${satelliteImgRatio.numerator} / ${satelliteImgRatio.total}` : ''}
                  style={getRatioStyle(satelliteImgRatio)}
                />
                <DetailBadgeItem
                  label={t('Anomaly Score')}
                  text={anomalyScore === null ? '' : String(anomalyScore)}
                  style={getAnomalyStyle(anomalyScore)}
                />
              </div>

              <div>
                <Typography.Title level={4}>{t('Hardware Status')}</Typography.Title>
                <DetailBadgeItem
                  label={t('Gateway Count')}
                  text={gatewayCounts ? `${gatewayCounts.online} / ${gatewayCounts.total}` : ''}
                  style={getCountStyle(gatewayCounts)}
                />
                <DetailBadgeItem
                  label={t('Node Count')}
                  text={nodeCounts ? `${nodeCounts.online} / ${nodeCounts.total}` : ''}
                  style={getCountStyle(nodeCounts)}
                />
                <DetailBadgeItem
                  label={t('Sensor Count')}
                  text={sensorCounts ? `${sensorCounts.online} / ${sensorCounts.total}` : ''}
                  style={getCountStyle(sensorCounts)}
                />
                <DetailItem
                  label={t('Block Count')}
                  value={getDisplayValue(ranchDetails?.blockCount)}
                />
              </div>

              <div>
                <Typography.Title level={4}>{t('Location')}</Typography.Title>
                <DetailItem
                  label={t('Region(country)')}
                  value={getDisplayValue(ranchDetails?.regionCountry)}
                />
                <DetailItem label={t('Location')} value={locationText} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RanchDetailPage;
